import client from "services/ngage/api-client";
import {Place, PlaceData} from "types/Place";
import deserializePlace from "./deserializers/place"
import DataResource from "types/DataResource";
import {snakeKeys} from "services/array";

export type PlaceFilters = {
    name?: string
    search?: string | null
    page?: number
    perPage?: number
    sort?: string
}

/**
 * Get all places that match the given filters (paginated)
 */
const filtered = async (filters: PlaceFilters = {}) => {
    const params = Object.assign({page: 1, per_page: 100}, snakeKeys(filters))

    try {
        const page = (await client.get<DataResource<Place> | null>('/places', {params})).data

        if (page) page.data = page.data.map(deserializePlace)

        return page
    } catch (error) {
        console.error("Unable to fetch places", error)
        return null
    }
}

/**
 * Get a single place
 *
 * @param {number} placeId
 */
const get = async (placeId: number) => {
    try {
        const place = (await client.get<Place | null>(`/places/${placeId}`)).data
        return place ? deserializePlace(place) : null
    } catch (error) {
        console.error(`Unable to fetch place ${placeId}`, error)
        return null
    }
}

/**
 * Store a new place
 *
 * @param data
 */
const store = async (data: PlaceData) => {
    try {
        const place = (await client.post<Place | null>('/places', data)).data
        return place ? deserializePlace(place) : null
    } catch (error) {
        console.error("Unable to store place", error)
        throw error
    }
}

/**
 * Update an existing place
 *
 * @param {number} placeId
 * @param data
 */
const update = async (placeId: number, data: PlaceData) => {
    try {
        const place = (await client.put<Place | null>(`/places/${placeId}`, data)).data
        return place ? deserializePlace(place) : null
    } catch (error) {
        console.error(`Unable to update place ${placeId}`, error)
        throw error
    }
}

/**
 * Permanently delete a place
 *
 * @param {number} placeId
 */
const destroy = async (placeId: number) => {
    try {
        return (await client.delete<null>(`/places/${placeId}`)).status === 204
    } catch (error) {
        console.error(`Unable to delete place ${placeId}`, error)
        throw error
    }
}

const places = {
    filtered,
    get,
    store,
    update,
    destroy
}

export default places;